import { Arrow, StickFigure } from "./Stickfigure";
import { RockContainer } from "./Rocks";
export interface AstroResults {
    element: string;
    mercuryRetrograde: boolean;
    moonPhaseName: string;
    moonPhaseIndex: number;
    personalBurden: number;
    othersBurden: number;
    rulingPlanet: string;
    rulingPlanetRetrograde: boolean;
    zodiacSign: string;
};

const planetMap: Record<string, string> = {
    "Aries": "Mars",
    "Taurus": "Venus",
    "Gemini": "Mercury",
    "Cancer": "Moon",
    "Leo": "Sun",
    "Virgo": "Mercury",
    "Libra": "Venus",
    "Scorpio": "Pluto",
    "Sagittarius": "Jupiter",
    "Capricorn": "Saturn",
    "Aquarius": "Uranus",
    "Pisces": "Neptune"
};

const planetCycles: Record<string, number> = {
    "Sun": 365,
    "Moon": 28,
    "Mercury": 88,
    "Venus": 225,
    "Mars": 687,
    "Jupiter": 4333,
    "Saturn": 10759,
    "Uranus": 30687,
    "Neptune": 60190,
    "Pluto": 90560
};

const moonPhaseNames = [
    "New Moon", "Waxing Crescent", "First Quarter", "Waxing Gibbous",
    "Full Moon", "Waning Gibbous", "Last Quarter", "Waning Crescent"
];

const mercuryRetrogradeStart = [
    { month: 1, day: 14 },
    { month: 5, day: 10 },
    { month: 9, day: 9 }
];
const mercuryRetrogradeDuration = 21; // days

export const getZodiacSign = (month: number, day: number): string => {
    if ((month === 3 && day >= 21) || (month === 4 && day <= 19)) return "Aries";
    if ((month === 4 && day >= 20) || (month === 5 && day <= 20)) return "Taurus";
    if ((month === 5 && day >= 21) || (month === 6 && day <= 20)) return "Gemini";
    if ((month === 6 && day >= 21) || (month === 7 && day <= 22)) return "Cancer";
    if ((month === 7 && day >= 23) || (month === 8 && day <= 22)) return "Leo";
    if ((month === 8 && day >= 23) || (month === 9 && day <= 22)) return "Virgo";
    if ((month === 9 && day >= 23) || (month === 10 && day <= 22)) return "Libra";
    if ((month === 10 && day >= 23) || (month === 11 && day <= 21)) return "Scorpio";
    if ((month === 11 && day >= 22) || (month === 12 && day <= 21)) return "Sagittarius";
    if ((month === 12 && day >= 22) || (month === 1 && day <= 19)) return "Capricorn";
    if ((month === 1 && day >= 20) || (month === 2 && day <= 18)) return "Aquarius";
    return "Pisces";
};

export const getElement = (sign: string): string => {
    if (sign === "Aries" || sign === "Leo" || sign === "Sagittarius") return "Fire";
    if (sign === "Taurus" || sign === "Virgo" || sign === "Capricorn") return "Earth";
    if (sign === "Gemini" || sign === "Libra" || sign === "Aquarius") return "Air";
    return "Water";
};

export const getPlanet = (sign: string): string => {
    return planetMap[sign];
};

export const getPlanetPositions = (today: Date): Record<string, number> => {
    const planetPositions: Record<string, number> = {};
    Object.keys(planetCycles).forEach(planet => {
        const startOffset = planet.length * 7;
        const dayOfYear = Math.floor((today.getTime() - new Date(today.getFullYear(), 0, 0).getTime()) / 86400000);
        planetPositions[planet] = ((dayOfYear + startOffset) % planetCycles[planet]) * (360 / planetCycles[planet]);
    });
    return planetPositions;
}

export const isRetrograde = (planet: string, today: Date): boolean => {
    const retroStart = (planetCycles[planet] * 0.4) % 360;
    const retroEnd = (planetCycles[planet] * 0.6) % 360;
    const planetPositions = getPlanetPositions(today);
    return planetPositions[planet] >= retroStart && planetPositions[planet] <= retroEnd;
};

export const getMoonPhase = (currentDate: number): [number, string] => {
    const moonPhaseDay = currentDate % 28;
    const moonPhaseIndex = Math.floor(moonPhaseDay / 3.5); // 0-7 (new, waxing crescent, first quarter, etc.)
    return [moonPhaseIndex, moonPhaseNames[moonPhaseIndex]];
}

export const getPersonalBurdenModifier = (element: string, moonPhaseIndex: number, mercuryRetrograde: boolean, rulingPlanetRetrograde: boolean, currentHour: number): number => {
    let personalBurdenModifier = 0;

    if (element === "Fire") personalBurdenModifier += 1;
    else if (element === "Earth") personalBurdenModifier -= 1;

    if (moonPhaseIndex === 4) personalBurdenModifier += 1; // Full moon increases burden
    else if (moonPhaseIndex === 0) personalBurdenModifier -= 1; // New moon decreases burden

    if (mercuryRetrograde) personalBurdenModifier += 1;

    if (rulingPlanetRetrograde) personalBurdenModifier += 1;

    if (currentHour < 6 || currentHour > 18) personalBurdenModifier += 1;

    return personalBurdenModifier;
}

const getPersonalMessage = (bucket: number, element: string, mercuryRetrograde: boolean, rulingPlanet: string, rulingPlanetRetrograde: boolean): string => {
    const personalMessages: Record<number, Record<string, string>> = {
        1: {
            Fire: "The stars align in your favor. The fiery energy of your sign creates minimal burden today.",
            Earth: "You're grounded and stable. Your earthly nature supports you, making any burden feel light as a pebble.",
            Air: "Your airy spirit flows freely. Cosmic alignments create minimal resistance to your path.",
            Water: "The cosmic tides are gentle. Your fluid nature helps you navigate with minimal burden."
        },
        2: {
            Fire: "Your fiery spirit carries a small stone of cosmic weight. Noticeable but not hindering your passionate drive.",
            Earth: "The earth element places a stone upon your path. Your practical nature helps you bear this modest weight.",
            Air: "Mental currents create a small stone of resistance. Your adaptive nature keeps you moving despite this weight.",
            Water: "Emotional currents bring a stone's weight. Your intuitive nature helps you flow around this obstacle."
        },
        3: {
            Fire: "Cosmic fires place a boulder in your path. Your determined nature will help you burn through this substantial challenge.",
            Earth: "The weight of a boulder tests your earthy resolve. Your practical strength will help you navigate around this obstacle.",
            Air: "Mental winds push a boulder against your progress. Your flexibility will help you find alternate paths forward.",
            Water: "Deep emotional currents bring a boulder to your shores. Your adaptability will help you flow around this challenge."
        },
        4: {
            Fire: "Cosmic forces align a mountain against your fiery progress. Even your passionate drive requires extraordinary focus to overcome.",
            Earth: "The weight of a cosmic mountain tests your foundations. Your practical strength meets its greatest challenge.",
            Air: "A mountain rises in your mental sky. Your flexible thinking faces its most demanding test.",
            Water: "A mountain diverts your deepest currents. Your adaptive nature encounters its most profound challenge."
        }
    };

    let personalMessage = personalMessages[bucket][element];

    if (mercuryRetrograde) {
        personalMessage += " Mercury retrograde intensifies communication challenges and misunderstandings.";
    }

    if (rulingPlanetRetrograde) {
        personalMessage += ` With ${rulingPlanet} in retrograde, your ruling planet's energy turns inward, requiring deeper reflection.`;
    }

    return personalMessage;
}

const getOtherMessage = (bucket: number, zodiacSign: string, moonPhaseIndex: number): string => {
    const otherMessages: Record<number, string> = {
        1: `Your ${zodiacSign} energy places only a pebble on those around you. Your presence is gentle and minimally demanding.`,
        2: `Your ${zodiacSign} nature creates the weight of a stone on others. Your influence is noticeable but not burdensome.`,
        3: `Your ${zodiacSign} influence weighs like a boulder on your community. Others feel the significant impact of your presence.`,
        4: `Your ${zodiacSign} force weighs like a mountain on others. Your presence creates profound and far-reaching effects on those in your orbit.`
    };
    let othersMessage = otherMessages[bucket];

    if (moonPhaseIndex === 4) { // Full moon
        othersMessage += " The full moon amplifies your emotional impact on others.";
    } else if (moonPhaseIndex === 0) { // New moon
        othersMessage += " The new moon tempers your outward influence, creating space for others to define their own path.";
    }

    return otherMessages[bucket];
};

interface AstroVizProps {
    astroResults: AstroResults;
}

export const AstroViz: React.FC<AstroVizProps> = ({ astroResults }) => {
    return (
        <div className="astro-content" style={{ overflow: 'hidden' }}>
            <div className="astro-header">
                <div className="astro-info">
                    <div className="astro-sign">Your Sign: {astroResults.zodiacSign}</div>
                    <div className="astro-details">Moon Phase: {astroResults.moonPhaseName}</div>
                    <div className="astro-details">{astroResults.mercuryRetrograde ? "Mercury in Retrograde" : "Mercury Direct"}</div>
                </div>
            </div>

            {/* Personal Burden */}
            <div className="astro-section">
                <h3 className="astro-section-title">Your Astrological Burden</h3>
                <div className="astro-burden-display">
                    <StickFigure color="#e6e6ff" />

                    <RockContainer rockIndex={astroResults.personalBurden} defaultLabel="Privilege" color="#a570db" gradientEnd="#6a30a5" />
                </div>

                <div className="astro-message">
                    {getPersonalMessage(astroResults.personalBurden, astroResults.element, astroResults.mercuryRetrograde, astroResults.rulingPlanet, astroResults.rulingPlanetRetrograde)}
                </div>
            </div>

            {/* Impact on Others */}
            <div className="astro-section">
                <h3 className="astro-section-title">Your Astrological Impact on Others</h3>
                <div className="astro-burden-display">
                    <StickFigure color="#e6e6ff" />
                    <Arrow color="#e6e6ff" />
                    <StickFigure color="#e6e6ff" />

                    <RockContainer rockIndex={astroResults.othersBurden} defaultLabel="None" color="#cc70db" gradientEnd="#9930a5" />
                </div>

                <div className="astro-message">
                    {getOtherMessage(astroResults.othersBurden, astroResults.zodiacSign, astroResults.moonPhaseIndex)}
                </div>
            </div>
        </div>
    );
}